/**
 * THE ONE SERVER CALL — declare the exhibit, write its lede, size its marks.
 *
 * `declareAskExhibit` picks the exhibit from the computation,
 * `composeAskStory` writes the lede from governed labels and binding tokens,
 * and `askExhibitGeometry` hands the canvas drawing ratios (never figures).
 * Composing them here keeps the API seam a one-liner and keeps all three
 * under the lib's own specs (docs/specs/ask-mbb-storyline-v1.md §3, #8842).
 */
import {
  declareAskExhibit,
  type AskDeclaredExhibitDto,
  type AskExhibitDeclarationInput,
} from './declared-exhibit';
import type { AskHelixNumericBindingDto, AskHelixRequestedOutputOutcomeDto } from '../../ask-helix';
import { askExhibitGeometry, askExhibitPlaceholders } from './declared-exhibit-geometry';
import { askStoryProblems, composeAskStory } from './declared-storyline';
import { selectNarrativeLens } from '../narrative/narrative-lens';
import { ASK_NARRATIVE_LENSES } from '../narrative/narrative-lenses';
import { memberDisplayLabel } from '../narrative/member-display-names';
import type { AskNarrationRank } from '../narrative/narrative-plan';
import { measureVoice } from '../narrative/measure-voice';

/** Slice 1b: one output's governed family and dataset (server-supplied; never on the wire). */
export type AskNarrationFamily = Readonly<{
  outputId: string;
  familyRef: string;
  datasetRef: string;
}>;

/**
 * `datasetRefs`: the governed datasets the answer's claims rest on, supplied by
 * the server from the private answer object (never from question text); they
 * select the domain lens. Absent → the plain grammar, no lens. `ranks`: the engine's rank
 * receipts (narration slice 1), also server-supplied; absent → no ranking sentence. `families`
 * (slice 1b): each output's governed family ref and dataset, server-supplied; they pick the
 * measure's voice file; absent, or a family without one → today's form.
 */
export function publishAskDeclaredExhibit(
  input: AskExhibitDeclarationInput,
  narration: Readonly<{
    datasetRefs?: readonly string[];
    ranks?: readonly AskNarrationRank[];
    families?: readonly AskNarrationFamily[];
  }> = {}
): AskDeclaredExhibitDto | null {
  const declared = declareAskExhibit(input);
  if (declared === null) return null;
  const bindings = input.numericBindings ?? [];
  const datasetRefs = narration.datasetRefs ?? [];
  const lens = selectNarrativeLens(ASK_NARRATIVE_LENSES, datasetRefs);
  // Publication-only member display names (never the reader catalog's labels).
  const displayLabel = (raw: string): string => memberDisplayLabel(datasetRefs, raw);
  const outputs = input.outputs ?? [];
  const output = (outputId: string) => outputs.find((entry) => entry.outputId === outputId);
  const family = (outputId: string) =>
    (narration.families ?? []).find((entry) => entry.outputId === outputId);
  const storyContext = {
    measure: (outputId: string) => output(outputId)?.requested.label ?? null,
    ranks: narration.ranks ?? [],
    groupBy: (outputId: string) => output(outputId)?.requested.groupBy ?? [],
    voice: (outputId: string) => {
      const entry = family(outputId);
      const voice = measureVoice(entry?.familyRef);
      return voice && entry ? { voice, datasetRef: entry.datasetRef } : null;
    },
  };
  const composed = composeAskStory(declared, bindings, lens, displayLabel, storyContext);
  // Publish a story only when it never restates the exhibit as text.
  const story =
    composed && askStoryProblems(composed, declared.exhibit).length === 0 ? composed : null;
  const context = sharedContext(askExhibitPlaceholders(declared.exhibit), bindings);
  return {
    ...declared,
    ...(story ? { story } : {}),
    geometry: askExhibitGeometry(declared.exhibit, bindings),
    context: { ...context, scope: context.scope === null ? null : displayLabel(context.scope) },
    modeledFigures: scenarioFigures(input.outputs ?? [], bindings),
  };
}

/** Operations whose figures a scenario or model computed (the only "modeled" ones). */
const SCENARIO_OPERATIONS: ReadonlySet<string> = new Set(['scenario_estimate']);

function sharedContext(
  placeholders: readonly string[],
  bindings: readonly AskHelixNumericBindingDto[]
): Readonly<{ period: string | null; scope: string | null }> {
  const drawn = bindings.filter((binding) => placeholders.includes(binding.placeholder));
  const shared = (values: readonly (string | undefined)[]): string | null => {
    const distinct = new Set(values);
    const [only] = distinct;
    return distinct.size === 1 && only ? only : null;
  };
  return {
    period: shared(drawn.map((binding) => binding.timeframe.label)),
    scope: shared(drawn.map((binding) => binding.scope.label)),
  };
}

function scenarioFigures(
  outputs: readonly AskHelixRequestedOutputOutcomeDto[],
  bindings: readonly AskHelixNumericBindingDto[]
): readonly string[] {
  const scenarioOutputs = new Set(
    outputs
      .filter((output) => SCENARIO_OPERATIONS.has(output.requested.operation))
      .map((output) => output.outputId)
  );
  return bindings
    .filter((binding) => binding.outputId !== undefined && scenarioOutputs.has(binding.outputId))
    .map((binding) => binding.placeholder);
}
